import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import LoadingSpinner from './LoadingSpinner';

// Debounce function for API calls
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const AppointmentForm = ({ onSuccess, editAppointment = null, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '09:00',
    duration: 60,
    description: '',
    email: '',
    phone: '',
    userName: '',
    locationType: 'office',
    location: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    virtualMeetingLink: '',
    locationNotes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Debounced location search
  const debouncedLocation = useDebounce(formData.location, 800);

  // Location suggestions
  useEffect(() => {
    const fetchLocationSuggestions = async () => {
      if (debouncedLocation.length > 2) {
        setIsLoadingLocation(true);
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(debouncedLocation)}&limit=5&countrycodes=us&addressdetails=1`
          );
          if (response.ok) {
            const data = await response.json();
            setLocationSuggestions(data);
          }
        } catch (error) {
          console.log('Could not fetch location suggestions');
        } finally {
          setIsLoadingLocation(false);
        }
      } else {
        setLocationSuggestions([]);
      }
    };

    fetchLocationSuggestions();
  }, [debouncedLocation]);

  useEffect(() => {
    if (editAppointment) {
      setFormData({
        title: editAppointment.title || '',
        date: editAppointment.date || '',
        time: editAppointment.time || '09:00',
        duration: editAppointment.duration || 60,
        description: editAppointment.description || '',
        email: editAppointment.email || '',
        phone: editAppointment.phone || '',
        userName: editAppointment.userName || '',
        locationType: editAppointment.locationType || 'office',
        location: editAppointment.location || '',
        address: editAppointment.address || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'USA'
        },
        virtualMeetingLink: editAppointment.virtualMeetingLink || '',
        locationNotes: editAppointment.locationNotes || ''
      });
    }
  }, [editAppointment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleLocationSelect = (suggestion) => {
    const address = suggestion.address;
    
    // Extract address components from the suggestion
    const street = address?.road || address?.street || '';
    const city = address?.city || address?.town || address?.village || address?.municipality || '';
    const state = address?.state || '';
    const zipCode = address?.postcode || '';
    const country = address?.country || 'USA';
    
    setFormData(prev => ({
      ...prev,
      location: suggestion.display_name,
      address: {
        street: street,
        city: city,
        state: state,
        zipCode: zipCode,
        country: country
      }
    }));
    setLocationSuggestions([]);
  };

  const clearLocationSearch = () => {
    setFormData(prev => ({
      ...prev,
      location: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      }
    }));
    setLocationSuggestions([]);
  };

  const sendAppointmentEmail = async (appointmentId) => {
    try {
      setSendingEmail(true);
      const response = await apiService.sendAppointmentEmail(appointmentId);
      if (response.success) {
        console.log('✅ Email sent successfully');
      }
    } catch (error) {
      console.error('❌ Error sending email:', error);
      // Don't throw error here - email failure shouldn't block appointment creation
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      if (editAppointment) {
        response = await apiService.updateAppointment(editAppointment._id, formData);
      } else {
        response = await apiService.createAppointment(formData);
      }

      if (response.success) {
        // Send email automatically after successful appointment creation
        if (response.data._id && formData.email) {
          await sendAppointmentEmail(response.data._id);
        }
        
        setSuccess(true);
        setTimeout(() => {
          setFormData({
            title: '',
            date: '',
            time: '09:00',
            duration: 60,
            description: '',
            email: '',
            phone: '',
            userName: '',
            locationType: 'office',
            location: '',
            address: {
              street: '',
              city: '',
              state: '',
              zipCode: '',
              country: 'USA'
            },
            virtualMeetingLink: '',
            locationNotes: ''
          });
          setSuccess(false);
          onSuccess(response.data);
        }, 3000);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error saving appointment');
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const calculateEndTime = (startTime, duration) => {
    const [hours, minutes] = startTime.split(':');
    const startDate = new Date();
    startDate.setHours(parseInt(hours), parseInt(minutes));
    const endDate = new Date(startDate.getTime() + duration * 60000);
    return formatTime(endDate.toTimeString().slice(0, 5));
  };

  const getFormProgress = () => {
    const requiredFields = ['title', 'date', 'time', 'userName', 'email', 'address.street', 'address.city', 'address.state', 'address.zipCode'];
    const filledFields = requiredFields.filter(field => {
      if (field.startsWith('address.')) {
        const addressField = field.split('.')[1];
        return formData.address[addressField]?.trim() !== '';
      }
      return formData[field]?.trim() !== '';
    }).length;
    return (filledFields / requiredFields.length) * 100;
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center animate-bounce-in">
          <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Appointment Scheduled! 🎉</h3>
          <p className="text-gray-600 mb-4">Your appointment has been added to the calendar.</p>
          {sendingEmail ? (
            <div className="flex items-center justify-center gap-2 text-blue-600 mb-4">
              <LoadingSpinner size="small" />
              <span className="text-sm">Sending confirmation email...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Confirmation email sent!</span>
            </div>
          )}
          <button 
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            onClick={() => setSuccess(false)}
          >
            Create Another Appointment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto border border-gray-200">
        {/* Enhanced Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-3xl p-6 text-white z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                {editAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}
              </h2>
              <p className="text-blue-100 mt-1">Fill in the details to schedule your appointment</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-all duration-200 group"
            >
              <svg className="w-6 h-6 group-hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="px-6 pt-6 bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Form Progress</span>
            <span className="text-sm font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
              {Math.round(getFormProgress())}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-500 ease-out shadow-inner"
              style={{ width: `${getFormProgress()}%` }}
            ></div>
          </div>
        </div>

        {/* Enhanced Error Message */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3 shadow-sm">
            <div className="flex-shrink-0 w-5 h-5 text-red-500 mt-0.5">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <span className="text-red-800 font-semibold block">Unable to Schedule</span>
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Enhanced Personal Information */}
          <div className="space-y-6 bg-gray-50 rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Personal Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                  Full Name *
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter your full name"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                  Email Address *
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="your.email@example.com"
                />
                <p className="text-xs text-blue-600 mt-1">
                  📧 Confirmation email will be sent to this address
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Enhanced Appointment Details */}
          <div className="space-y-6 bg-gray-50 rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Appointment Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                  Appointment Title *
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Meeting title or purpose"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                  Date *
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min={getTodayDate()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center">
                  Time *
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Duration</label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                  <option value={180}>3 hours</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white resize-none"
                placeholder="Brief description of the appointment purpose, agenda, or special requirements..."
              />
            </div>
          </div>

          {/* Enhanced Location Details */}
          <div className="space-y-6 bg-gray-50 rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Location Details</h3>
            </div>
            
            {/* Location Type */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Location Type</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { value: 'office', label: '🏢 Office', desc: 'In-office meeting' },
                  { value: 'showroom', label: '🛋️ Showroom', desc: 'Showroom visit' },
                  { value: 'client-site', label: '🏠 Client Site', desc: 'On-site consultation' },
                  { value: 'virtual', label: '💻 Virtual', desc: 'Online meeting' },
                  { value: 'other', label: '❓ Other', desc: 'Other location' }
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, locationType: type.value }))}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-center group ${
                      formData.locationType === type.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="text-lg mb-1">{type.label.split(' ')[0]}</div>
                    <div className="text-xs opacity-75 group-hover:opacity-100">{type.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Location Search */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Quick Address Search
                <span className="text-xs text-gray-500 font-normal ml-2">(Optional - auto-fills address fields)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white pr-12"
                  placeholder="Search for address (e.g., '123 Main St, New York')"
                />
                {formData.location && (
                  <button
                    type="button"
                    onClick={clearLocationSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {isLoadingLocation && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <LoadingSpinner size="small" />
                  </div>
                )}
              </div>

              {/* Location Suggestions */}
              {locationSuggestions.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-xl bg-white shadow-lg max-h-48 overflow-y-auto">
                  {locationSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleLocationSelect(suggestion)}
                      className="p-4 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {suggestion.display_name}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Enhanced Address Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                  Street Address *
                  {formData.address.street && (
                    <span className="ml-2 text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">✓ Complete</span>
                  )}
                </label>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter street address"
                  required
                />
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                  City *
                  {formData.address.city && (
                    <span className="ml-2 text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">✓ Complete</span>
                  )}
                </label>
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter city"
                  required
                />
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                  State *
                  {formData.address.state && (
                    <span className="ml-2 text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">✓ Complete</span>
                  )}
                </label>
                <input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter state"
                  required
                />
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                  ZIP Code *
                  {formData.address.zipCode && (
                    <span className="ml-2 text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">✓ Complete</span>
                  )}
                </label>
                <input
                  type="text"
                  name="address.zipCode"
                  value={formData.address.zipCode}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter ZIP code"
                  required
                />
              </div>
              
              <div className="md:col-span-2 space-y-3">
                <label className="block text-sm font-semibold text-gray-700">Country *</label>
                <select
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  required
                >
                  <option value="USA">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Helper Text */}
              <div className="md:col-span-2">
                <div className="bg-white border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700 flex items-center">
                    <span className="text-lg mr-2">💡</span>
                    <strong>Pro Tip:</strong> Use the search above to auto-fill these fields, or manually enter the address details. 
                    All fields marked with * are required for appointment confirmation.
                  </p>
                </div>
              </div>
            </div>

            {/* Virtual Meeting Link */}
            {formData.locationType === 'virtual' && (
              <div className="space-y-3 bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <label className="block text-sm font-semibold text-gray-700 flex items-center">
                  Virtual Meeting Link *
                  {formData.virtualMeetingLink && (
                    <span className="ml-2 text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">✓ Complete</span>
                  )}
                </label>
                <input
                  type="url"
                  name="virtualMeetingLink"
                  value={formData.virtualMeetingLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="https://meet.google.com/abc-xyz-123"
                  required
                />
                <p className="text-xs text-green-700 mt-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Please provide the full URL for Zoom, Google Meet, Teams, or other virtual meeting platforms
                </p>
              </div>
            )}

            {/* Location Notes */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Additional Location Notes
                <span className="text-xs text-gray-500 font-normal ml-2">(Optional)</span>
              </label>
              <textarea
                name="locationNotes"
                value={formData.locationNotes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white resize-none"
                placeholder="Parking instructions, building access codes, floor numbers, special directions, or any other location-specific information..."
              />
              <p className="text-xs text-gray-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Help us and your appointment attendees with any specific location instructions
              </p>
            </div>

            {/* Address Validation Summary */}
            {(formData.address.street || formData.address.city || formData.address.state || formData.address.zipCode) && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-3 text-green-800">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-semibold block">Address Details Added</span>
                    <div className="text-xs text-green-700 mt-1">
                      {formData.address.street && formData.address.city && formData.address.state && formData.address.zipCode 
                        ? "🎉 All required address fields are complete! Your appointment is ready to be scheduled."
                        : "📝 Some address fields are still missing. Please fill all required fields marked with *."}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center pt-8 border-t border-gray-200">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Confirmation email will be sent automatically</span>
            </div>
            
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || getFormProgress() < 100}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:hover:scale-100"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Scheduling Appointment...</span>
                  </>
                ) : sendingEmail ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Sending Email...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{editAppointment ? 'Update Appointment' : 'Schedule Appointment & Send Email'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;